/**
 * Moving a resource to a different folder.
 *
 * The destination is chosen by **searching** rather than by browsing a tree,
 * which is the unusual choice here and a deliberate one: the search endpoint
 * already filters to what the caller may read, so a picker built on it cannot
 * offer a folder they cannot see. A hand-rolled tree would have to re-derive
 * that, and would get it wrong the first time somebody had read access to a
 * folder but not to its parent.
 *
 * Search is scoped to the resource's own library — a move across libraries is
 * not a move, and the server would refuse it.
 */
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import FolderIcon from '@mui/icons-material/Folder';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ResourceDto } from '@forsys/shared';
import { FormDialog } from '../../components/FormDialog';
import { useAsync } from '../../hooks/useAsync';
import { useDebounced } from '../../hooks/useDebounced';
import { useSubmit } from '../../hooks/useSubmit';
import { searchResources, updateResource } from '../../api/resources';

export interface MoveDialogProps {
  resource: ResourceDto;
  open: boolean;
  onClose: () => void;
  onMoved: () => void;
}

export function MoveDialog({ resource, open, onClose, onMoved }: MoveDialogProps) {
  const { t } = useTranslation('files');
  const [term, setTerm] = useState('');
  const [destination, setDestination] = useState<ResourceDto | null>(null);
  const debounced = useDebounced(term);

  const folders = useAsync(
    () => searchResources({ q: debounced, kind: 'folder', library: resource.library, limit: 25 }),
    [debounced, resource.library],
    { enabled: open && debounced.trim().length > 0 },
  );

  const move = useSubmit(async () => {
    if (!destination) return undefined;
    const result = await updateResource(resource.id, { parentId: destination.id });
    onMoved();
    return result;
  });

  // A folder cannot be moved into itself. Its own descendants are the other
  // illegal destination, and the server rejects those — working out the subtree
  // here would mean walking it, and it would still be a guess by the time the
  // request arrived.
  const options = (folders.data?.results ?? []).filter(
    (candidate) => candidate.id !== resource.id && candidate.capabilities.write,
  );

  return (
    <FormDialog
      open={open}
      title={t('move.title', { name: resource.name })}
      submitLabel={t('move.submit')}
      pending={move.pending}
      error={move.error}
      disabled={!destination}
      onClose={onClose}
      onSubmit={async () => {
        const moved = await move.submit();
        if (moved) onClose();
      }}
    >
      <Autocomplete
        options={options}
        value={destination}
        loading={folders.loading}
        filterOptions={(all) => all}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={(_event, selected) => setDestination(selected)}
        onInputChange={(_event, input) => setTerm(input)}
        noOptionsText={
          debounced.trim() === '' ? t('move.typeToSearch') : t('move.noMatches')
        }
        renderOption={(props, option) => {
          const { key: _key, ...liProps } = props;
          return (
            <Box component="li" key={option.id} {...liProps} sx={{ gap: 1.5 }}>
              <FolderIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <ListItemText
                primary={option.name}
                secondary={t('move.option', {
                  library: option.library,
                  depth: option.depth,
                })}
                slotProps={{ secondary: { variant: 'caption' } }}
              />
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('move.destination')}
            placeholder={t('move.placeholder')}
            autoFocus
            slotProps={{
              // Spread params.slotProps first — see SubjectPicker: overriding
              // `input` alone drops `htmlInput` and the field stops typing.
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {folders.loading && <CircularProgress size={16} />}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />

      <Typography variant="caption" color="text.secondary">
        {/* Trans, not t: the library name is emphasised mid-sentence, and where
            that emphasis falls is part of the translation, not of this markup. */}
        <Trans
          i18nKey="move.scopeNote"
          ns="files"
          values={{ library: resource.library }}
          components={{ strong: <strong /> }}
        />
      </Typography>
    </FormDialog>
  );
}
